import { useNavigate } from '@tanstack/react-router';
import { useMemo } from 'react';

import type { SidebarViewRow } from '@flow/sync';

import { t } from '../i18n/es.js';
import { useSession } from '../session.jsx';
import { useChooseTheme } from '../theme/ThemeControl.jsx';

import { viewHref } from './sections.js';

/**
 * Registry that feeds the command palette and the shortcut sheet (REC-07). Every later change
 * adds its own commands here —capture, triage, views— so both stay in step without a second list.
 */

export interface Command {
  id: string;
  /** What the palette shows and what the typed text is matched against. */
  title: string;
  /** Group heading in the palette, e.g. «Ir a» or «Acciones». */
  group: string;
  /** Shortcut hint, shown in the palette and in the sheet. */
  hint?: string;
  run: () => void;
}

export interface CommandsInput {
  views: SidebarViewRow[];
  openShortcuts: () => void;
  openCapture: () => void;
  toggleSidebar: () => void;
}

/** Navigation shortcuts of docs/05 §6.1 that already have a destination. */
export const GOTO_KEYS: Record<string, string> = {
  i: 'inbox',
  a: 'asap',
  b: 'backlog',
  s: 'waiting',
  p: 'projects',
  r: 'resources',
};

export function useCommands({
  views,
  openShortcuts,
  openCapture,
  toggleSidebar,
}: CommandsInput): Command[] {
  const navigate = useNavigate();
  const { signOut, local, user } = useSession();
  const setTheme = useChooseTheme(local, user?.id);

  return useMemo(() => {
    const goToKey = (viewKey: string) =>
      Object.entries(GOTO_KEYS).find(([, key]) => key === viewKey)?.[0];

    const navigation: Command[] = views
      .filter((view) => view.key)
      .map((view) => {
        const key = view.key!;
        const letter = goToKey(key);
        return {
          id: `goto:${key}`,
          title: view.name,
          group: t.shell.commands.goTo,
          ...(letter ? { hint: `G ${letter.toUpperCase()}` } : {}),
          run: () => void navigate({ to: viewHref(key) }),
        };
      });

    // The Trash is not a view but is reached the same way (f1-trash-search D8).
    navigation.push({
      id: 'goto:trash',
      title: t.trash.goTo,
      group: t.shell.commands.goTo,
      run: () => void navigate({ to: '/trash' }),
    });

    const actions: Command[] = [
      {
        id: 'capture',
        title: t.shell.commands.capture,
        group: t.shell.commands.actions,
        hint: 'N',
        run: openCapture,
      },
      {
        id: 'triage',
        title: t.shell.commands.triage,
        group: t.shell.commands.actions,
        run: () => void navigate({ to: '/triage' }),
      },
      {
        id: 'theme:light',
        title: t.shell.commands.themeLight,
        group: t.shell.commands.actions,
        run: () => setTheme('light'),
      },
      {
        id: 'theme:dark',
        title: t.shell.commands.themeDark,
        group: t.shell.commands.actions,
        run: () => setTheme('dark'),
      },
      {
        id: 'theme:system',
        title: t.shell.commands.themeSystem,
        group: t.shell.commands.actions,
        run: () => setTheme('system'),
      },
      {
        id: 'sidebar:toggle',
        title: t.shell.commands.toggleSidebar,
        group: t.shell.commands.actions,
        run: toggleSidebar,
      },
      {
        id: 'shortcuts',
        title: t.shell.commands.shortcuts,
        group: t.shell.commands.actions,
        hint: '?',
        run: openShortcuts,
      },
      {
        id: 'signout',
        title: t.session.signOut,
        group: t.shell.commands.actions,
        run: () => void signOut(),
      },
    ];

    return [...navigation, ...actions];
  }, [views, navigate, setTheme, signOut, openShortcuts, openCapture, toggleSidebar]);
}

/** Matching used by the palette: accent- and case-insensitive, by substring. */
export function matchesCommand(command: Command, term: string): boolean {
  if (term.trim() === '') return true;
  return normalise(command.title).includes(normalise(term));
}

function normalise(value: string): string {
  return value
    .normalize('NFD')
    .replace(/\p{Diacritic}/gu, '')
    .toLowerCase();
}
